import { Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import {
  FestivalModuleCategory,
  getModuleSettings,
  updateModuleSettings,
} from '@/core/services/module-settings'
import { IFestivalMenuItem } from '@/core/services/menu'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'

export interface EditModulesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  festival?: IFestivalMenuItem
}

const humanize = (name: string) =>
  name
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const allModulesEnabled = (categories: FestivalModuleCategory[]) =>
  categories.every((c) => c.modules.every((m) => m.enabled))

export const EditModulesModal = ({
  open,
  onOpenChange,
  festival,
}: EditModulesModalProps): JSX.Element => {
  const [categories, setCategories] = useState<FestivalModuleCategory[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    if (e instanceof Error) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage(e.message)
      setErrorStatus(
        (e as { response?: { status?: number } })?.response?.status,
      )
    } else {
      console.error(e)
    }
  }

  useEffect(() => {
    if (!open || !festival) return
    setSearch('')
    const getData = async () => {
      try {
        setLoading(true)
        const { data } = await getModuleSettings(festival.platformName)
        setCategories(data)
      } catch (e) {
        handleError(e)
      } finally {
        setLoading(false)
      }
    }
    getData()
  }, [open, festival])

  const setModulesEnabled = (
    predicate: (category: FestivalModuleCategory) => boolean,
    enabled: boolean,
  ) => {
    setCategories((prev) =>
      prev.map((category) =>
        predicate(category)
          ? {
              ...category,
              modules: category.modules.map((m) => ({ ...m, enabled })),
            }
          : category,
      ),
    )
  }

  const toggleModule = (categoryName: string, moduleId: string) => {
    setCategories((prev) =>
      prev.map((category) =>
        category.category === categoryName
          ? {
              ...category,
              modules: category.modules.map((m) =>
                m.id === moduleId ? { ...m, enabled: !m.enabled } : m,
              ),
            }
          : category,
      ),
    )
  }

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return categories
    return categories
      .map((category) => ({
        ...category,
        modules: category.modules.filter(
          (m) =>
            humanize(m.name).toLowerCase().includes(term) ||
            category.category.toLowerCase().includes(term),
        ),
      }))
      .filter((category) => category.modules.length > 0)
  }, [categories, search])

  const allChecked = useMemo(() => allModulesEnabled(categories), [categories])

  const handleSave = async () => {
    if (!festival) return
    try {
      setSaving(true)
      const payload = categories.flatMap((c) =>
        c.modules.map((m) => ({ id: m.id, name: m.name, enabled: m.enabled })),
      )
      await updateModuleSettings(festival.platformName, payload)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Modules updated successfully')
      onOpenChange(false)
    } catch (e) {
      handleError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        size="md"
        headerClassName="mb-5"
        title={
          <div className="flex items-center gap-3 border-b border-monochrome-120 pb-4">
            <PageTitle
              title={
                <span className="text-xl font-bold text-monochrome-1">
                  Edit Modules for{' '}
                  <span className="text-accent-1">{festival?.name}</span>
                </span>
              }
              icon="modules"
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2 border-t border-monochrome-120 pt-4">
            <Button
              onClick={() => onOpenChange(false)}
              type="button"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              type="button"
              variant="primary"
              loading={saving}
              disabled={saving || loading}
            >
              Save
            </Button>
          </div>
        }
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Modules ..."
          prefix={<Search className="h-4 w-4 text-monochrome-2" />}
          className="rounded-full !bg-transparent"
        />

        {loading ? (
          <Loader />
        ) : (
          <div className="mt-5 space-y-3">
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={allChecked}
                onCheckedChange={(checked) =>
                  setModulesEnabled(() => true, Boolean(checked))
                }
              />
              <span className="text-button-m font-semibold text-monochrome-1">
                All Modules
              </span>
            </label>

            {filteredCategories.map((category) => {
              const categoryChecked = category.modules.every((m) => m.enabled)
              const isSingle =
                category.modules.length === 1 &&
                humanize(category.modules[0].name) ===
                  humanize(category.category)

              if (isSingle) {
                const module = category.modules[0]
                return (
                  <label
                    key={category.category}
                    className="flex cursor-pointer items-center gap-2.5"
                  >
                    <Checkbox
                      checked={module.enabled}
                      onCheckedChange={() =>
                        toggleModule(category.category, module.id)
                      }
                    />
                    <span className="text-button-m font-semibold text-monochrome-1">
                      {humanize(category.category)}
                    </span>
                  </label>
                )
              }

              return (
                <div key={category.category} className="space-y-3">
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <Checkbox
                      checked={categoryChecked}
                      onCheckedChange={(checked) =>
                        setModulesEnabled(
                          (c) => c.category === category.category,
                          Boolean(checked),
                        )
                      }
                    />
                    <span className="text-button-m font-semibold text-monochrome-1">
                      {humanize(category.category)}
                    </span>
                  </label>
                  <div className="space-y-3 pl-6">
                    {category.modules.map((module) => (
                      <label
                        key={module.id}
                        className="flex cursor-pointer items-center gap-2.5"
                      >
                        <Checkbox
                          checked={module.enabled}
                          onCheckedChange={() =>
                            toggleModule(category.category, module.id)
                          }
                        />
                        <span className="text-body-medium text-monochrome-1">
                          {humanize(module.name)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
